import type { RefObject } from 'react';
import { useCallback, useEffect, useRef } from 'react';

const DEFAULT_THRESHOLD: number[] = [0, 0.1];

export interface ViewportVisibilityOptions {
  elementSelector: string;
  onVisibilityChange?: (visibleIds: Set<number>) => void;
  onElementVisible?: (element: HTMLElement, id: number) => void;
  onElementHidden?: (element: HTMLElement, id: number) => void;
  root?: Element | null;
  rootRef?: RefObject<Element | null>;
  rootMargin?: string;
  threshold?: number | number[];
  idAttribute?: string;
  throttleMs?: number;
  dependencies?: unknown[];
}

export function useViewportVisibility<T extends HTMLElement = HTMLDivElement>(
  options: ViewportVisibilityOptions,
) {
  const {
    elementSelector,
    onVisibilityChange,
    onElementVisible,
    onElementHidden,
    root,
    rootRef,
    rootMargin = '200px 0px 200px 0px',
    threshold = DEFAULT_THRESHOLD,
    idAttribute = 'data-chunk-id',
    throttleMs = 150,
    dependencies = [],
  } = options;

  const containerRef = useRef<T>(null);
  const visibleIdsRef = useRef<Set<number>>(new Set());
  const observerRef = useRef<IntersectionObserver | null>(null);
  const lastRunTimeRef = useRef<number>(0);
  const timeoutRef = useRef<number | null>(null);

  const callbacksRef = useRef({
    onVisibilityChange,
    onElementVisible,
    onElementHidden,
  });

  useEffect(() => {
    callbacksRef.current = {
      onVisibilityChange,
      onElementVisible,
      onElementHidden,
    };
  }, [onVisibilityChange, onElementVisible, onElementHidden]);

  const flushUpdates = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    lastRunTimeRef.current = Date.now();
    const currentIds = new Set(visibleIdsRef.current);
    callbacksRef.current.onVisibilityChange?.(currentIds);
  }, []);

  const scheduleUpdate = useCallback(() => {
    const now = Date.now();
    const timeSinceLastRun = now - lastRunTimeRef.current;

    if (timeoutRef.current) return;

    if (timeSinceLastRun >= throttleMs) {
      flushUpdates();
    } else {
      const delay = throttleMs - timeSinceLastRun;
      timeoutRef.current = window.setTimeout(flushUpdates, delay);
    }
  }, [flushUpdates, throttleMs]);

  useEffect(() => {
    if (!containerRef.current) return;

    if (observerRef.current) observerRef.current.disconnect();

    const rootElement = rootRef?.current ?? root ?? null;
    const observer = new IntersectionObserver(
      (entries) => {
        let hasChanges = false;

        entries.forEach((entry) => {
          const element = entry.target as HTMLElement;
          const idStr = element.getAttribute(idAttribute);
          if (!idStr) return;

          const id = Number.parseInt(idStr, 10);
          if (Number.isNaN(id)) return;

          const isVisible = entry.isIntersecting;
          const wasVisible = visibleIdsRef.current.has(id);

          if (isVisible && !wasVisible) {
            visibleIdsRef.current.add(id);
            callbacksRef.current.onElementVisible?.(element, id);
            hasChanges = true;
          } else if (!isVisible && wasVisible) {
            visibleIdsRef.current.delete(id);
            callbacksRef.current.onElementHidden?.(element, id);
            hasChanges = true;
          }
        });

        if (hasChanges) {
          scheduleUpdate();
        }
      },
      {
        root: rootElement,
        rootMargin,
        threshold,
      },
    );

    observerRef.current = observer;

    const elements = containerRef.current.querySelectorAll(elementSelector);
    elements.forEach((el) => {
      observer.observe(el);
    });

    return () => {
      observer.disconnect();
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [
    elementSelector,
    root,
    rootRef,
    rootMargin,
    idAttribute,
    scheduleUpdate,
    ...dependencies,
    threshold,
  ]);

  return containerRef;
}
